import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { DialogBoundryService } from '@secca/core/services/dialog-boundry.service';
import { parseAtlasCsvRows } from '@secca/shared/helpers/atlas/atlas-csv-parser.helper';

interface FraudDetectionRuleRow {
  value: string;
  score: string;
  note: string;
}

interface SupplierScoreRuleRow {
  recommendation: string;
  score: string;
  flagged: boolean;
}

type FraudDetectionRulesTab = 'timeDeparture' | 'timeArrival' | 'reserveScores' | 'supplierScores';

@Component({
  selector: 'app-atlas-fraud-detection-rules-dialog',
  templateUrl: './atlas-fraud-detection-rules-dialog.component.html',
  styleUrls: ['./atlas-fraud-detection-rules-dialog.component.scss'],
  standalone: false
})
export class AtlasFraudDetectionRulesDialogComponent implements OnInit {
  private readonly timeDepartureRulesCsvPath = 'assets/atlas-chatbot/fraud-detection-rules/time_departure_rules.csv';
  private readonly timeArrivalRulesCsvPath = 'assets/atlas-chatbot/fraud-detection-rules/time_arrival_rules.csv';
  private readonly reserveScoresRulesCsvPath = 'assets/atlas-chatbot/fraud-detection-rules/reserve_scores_rules.csv';
  private readonly supplierScoresCsvPath = 'assets/atlas-chatbot/fraud-detection-rules/supplier_scores.csv';

  activeTab: FraudDetectionRulesTab = 'timeDeparture';
  timeDepartureRows: FraudDetectionRuleRow[] = [];
  timeArrivalRows: FraudDetectionRuleRow[] = [];
  reserveScoreRows: FraudDetectionRuleRow[] = [];
  supplierScoreRows: SupplierScoreRuleRow[] = [];
  isLoadingTimeDepartureRules = true;
  isLoadingTimeArrivalRules = true;
  isLoadingReserveScoreRules = true;
  isLoadingSupplierScoreRules = true;
  timeDepartureRulesLoadFailed = false;
  timeArrivalRulesLoadFailed = false;
  reserveScoreRulesLoadFailed = false;
  supplierScoreRulesLoadFailed = false;

  constructor(
    private dialogRef: MatDialogRef<AtlasFraudDetectionRulesDialogComponent>,
    public dialogBoundryService: DialogBoundryService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.http.get(this.timeDepartureRulesCsvPath, { responseType: 'text' }).subscribe({
      next: csv => {
        this.timeDepartureRows = this.parseTimeDepartureRules(csv);
        this.isLoadingTimeDepartureRules = false;
        this.timeDepartureRulesLoadFailed = false;
      },
      error: () => {
        this.timeDepartureRows = [];
        this.isLoadingTimeDepartureRules = false;
        this.timeDepartureRulesLoadFailed = true;
      }
    });

    this.http.get(this.timeArrivalRulesCsvPath, { responseType: 'text' }).subscribe({
      next: csv => {
        this.timeArrivalRows = this.parseTimeArrivalRules(csv);
        this.isLoadingTimeArrivalRules = false;
        this.timeArrivalRulesLoadFailed = false;
      },
      error: () => {
        this.timeArrivalRows = [];
        this.isLoadingTimeArrivalRules = false;
        this.timeArrivalRulesLoadFailed = true;
      }
    });

    this.http.get(this.reserveScoresRulesCsvPath, { responseType: 'text' }).subscribe({
      next: csv => {
        this.reserveScoreRows = this.parseReserveScoreRules(csv);
        this.isLoadingReserveScoreRules = false;
        this.reserveScoreRulesLoadFailed = false;
      },
      error: () => {
        this.reserveScoreRows = [];
        this.isLoadingReserveScoreRules = false;
        this.reserveScoreRulesLoadFailed = true;
      }
    });

    this.http.get(this.supplierScoresCsvPath, { responseType: 'text' }).subscribe({
      next: csv => {
        this.supplierScoreRows = this.parseSupplierScoreRules(csv);
        this.isLoadingSupplierScoreRules = false;
        this.supplierScoreRulesLoadFailed = false;
      },
      error: () => {
        this.supplierScoreRows = [];
        this.isLoadingSupplierScoreRules = false;
        this.supplierScoreRulesLoadFailed = true;
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  setActiveTab(tab: FraudDetectionRulesTab): void {
    this.activeTab = tab;
  }

  private parseTimeDepartureRules(csv: string): FraudDetectionRuleRow[] {
    return this.parseRuleRows(csv, 3, columns => ({
      value: columns[0],
      score: columns[1],
      note: columns[2]
    }));
  }

  private parseTimeArrivalRules(csv: string): FraudDetectionRuleRow[] {
    return this.parseRangeRules(csv);
  }

  private parseReserveScoreRules(csv: string): FraudDetectionRuleRow[] {
    return this.parseRangeRules(csv);
  }

  private parseSupplierScoreRules(csv: string): SupplierScoreRuleRow[] {
    return this.parseRuleRows(csv, 3, columns => ({
      recommendation: columns[0],
      score: columns[1],
      flagged: columns[2] === '1'
    }));
  }

  private parseRangeRules(csv: string): FraudDetectionRuleRow[] {
    return this.parseRuleRows(csv, 4, columns => ({
      value: this.formatRange(columns[0], columns[1]),
      score: columns[2],
      note: columns[3]
    }));
  }

  private parseRuleRows<T>(csv: string, minimumColumnCount: number, mapRow: (columns: string[]) => T): T[] {
    return parseAtlasCsvRows(csv, minimumColumnCount).map(mapRow);
  }

  private formatRange(low: string, high: string): string {
    return low === high ? low : `${low}-${high}`;
  }
}
